import { useState } from "react";
import { api } from "../api";
import { ErrorState } from "../ui/State";
import type { CashGroupMapping, Evidence, Packet, StructureResult } from "./xero-history-import-types";

export function XeroCashGroups({
  packet,
  structure,
  evidence,
  reason,
  root,
  busy,
  setBusy,
  stop,
}: {
  packet: Packet;
  structure: StructureResult | undefined;
  evidence: (checksum: string) => Omit<Evidence, "title">;
  reason: string;
  root: string;
  busy: boolean;
  setBusy: (value: boolean) => void;
  stop: { current: boolean };
}) {
  const groups = packet.cashGroupMappings ?? [],
    [results, setResults] = useState<Record<string, { groupId?: string; error?: unknown }>>({}),
    [page, setPage] = useState(0);
  const key = (g: CashGroupMapping) =>
    g.payments
      .map((p) => p.sourceId)
      .sort()
      .join(",");
  async function copy() {
    if (!structure) return;
    setBusy(true);
    stop.current = false;
    const period = (date: string) => {
      const found = structure!.periods.filter((p) => p.startsOn <= date && p.endsOn >= date);
      if (found.length !== 1) throw Error("Prepare one original full-year period for every source date.");
      return found[0]!.id;
    };
    try {
      for (const g of groups) {
        if (stop.current) break;
        if (results[key(g)]?.groupId) continue;
        try {
          const payments = g.payments.map((m) => {
            const cash = packet.history.payments?.find((p) => p.sourceId === m.sourceId);
            if (!cash) throw Error("Retain the exact original grouped receipt.");
            const accounts = [
              ...new Set(
                cash.applications.map(
                  (a) => structure.accounts.find((s) => s.sourceIds.includes(a.invoiceSourceId))?.accountId,
                ),
              ),
            ];
            if (accounts.length !== 1 || !accounts[0])
              throw Error("Prepare one original property account for every invoice settled by this receipt.");
            return {
              sourceId: m.sourceId,
              accountId: accounts[0],
              periodId: period(cash.date),
              evidence: evidence(m.sourceChecksum),
            };
          });
          const fees = g.fees.map((m) => {
            const date = (
              packet.history.events.find((e) => e.sourceId === m.sourceId)?.lines[0] as { date?: string } | undefined
            )?.date;
            if (!date) throw Error("Retain the original fee date.");
            return {
              sourceId: m.sourceId,
              costCodeId: m.costCodeId,
              periodId: period(date),
              evidence: evidence(m.sourceChecksum),
            };
          });
          const sources = new Set([...g.payments, ...g.fees].map((s) => s.sourceId)),
            originalPayments = packet.history.payments?.filter((p) => sources.has(p.sourceId)) ?? [],
            invoiceIds = new Set(originalPayments.flatMap((p) => p.applications.map((a) => a.invoiceSourceId)));
          const result = await api<{ groupId: string }>(root + "/cash-groups", {
            method: "POST",
            body: JSON.stringify({
              history: {
                organisation: packet.history.organisation,
                selector: packet.history.selector,
                buildingId: packet.history.buildingId,
                accountMappings: packet.history.accountMappings ?? [],
                events: packet.history.events.filter((e) => sources.has(e.sourceId) || invoiceIds.has(e.sourceId)),
                invoices: packet.history.invoices.filter((i) => invoiceIds.has(i.sourceId)),
                payments: originalPayments,
              },
              bankAccountId: g.bankAccountId,
              scheduleId: structure.scheduleId,
              payments,
              fees,
              nativeTransactionIds: g.nativeTransactionIds,
              bankWitnesses: g.bankWitnesses.map(({ checksum, ...w }) => ({ ...w, evidence: evidence(checksum) })),
              reason,
            }),
          });
          setResults((r) => ({ ...r, [key(g)]: result }));
        } catch (error) {
          setResults((r) => ({ ...r, [key(g)]: { error } }));
        }
      }
    } finally {
      setBusy(false);
    }
  }
  return (
    <section aria-label="Original grouped bank receipts">
      <h2>Original grouped bank receipts</h2>
      <p>
        {groups.length} bank groups · {Object.values(results).filter((r) => r.groupId).length} confirmed in this
        session.
      </p>
      <p>
        Each original receipt, fee and invoice allocation keeps its own identity and date. The bank pool is linked as a
        group where individual correspondences are unknown.
      </p>
      <button disabled={busy || !structure || reason.trim().length < 5} onClick={() => void copy()}>
        Copy original grouped receipts and fees
      </button>
      <table>
        <thead>
          <tr>
            <th>Original group</th>
            <th>Receipts</th>
            <th>Fees</th>
            <th>Import result</th>
          </tr>
        </thead>
        <tbody>
          {groups.slice(page * 25, (page + 1) * 25).map((g) => (
            <tr key={key(g)}>
              <td>{g.reference}</td>
              <td>{g.payments.length}</td>
              <td>{g.fees.length}</td>
              <td>
                {results[key(g)]?.groupId ? (
                  "Copied"
                ) : results[key(g)]?.error ? (
                  <ErrorState error={results[key(g)]?.error} />
                ) : (
                  "Ready for source checks"
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {groups.length > 25 && (
        <nav aria-label="Original bank group pages">
          <button disabled={!page} onClick={() => setPage(page - 1)}>
            Previous
          </button>{" "}
          Page {page + 1} of {Math.ceil(groups.length / 25)}{" "}
          <button disabled={(page + 1) * 25 >= groups.length} onClick={() => setPage(page + 1)}>
            Next
          </button>
        </nav>
      )}
    </section>
  );
}
